import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import './styles/logo.css'; // Import logo styles
import './styles/animations.css'; // Import dashboard animations
import Login from './pages/auth/Login';
import ProtectedRoute from './components/ProtectedRoute';
import Unauthorized from './components/Unauthorized';
import EmployeeDashboard from './pages/employee/EmployeeDashboard';
import CustomerManagement from './pages/employee/CustomerManagement';
import FranchiseManagement from './pages/employee/FranchiseManagement';
import BranchManagement from './pages/employee/BranchManagement';
import UserManagement from './pages/employee/UserManagement';
import LocationManagement from './pages/employee/LocationManagement';
import ZoneManagement from './pages/employee/ZoneManagement';
import RegionManagement from './pages/employee/RegionManagement';
import { regionsAreUsed } from './services/rateEngine';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import FranchiseDashboard from './pages/franchise/FranchiseDashboard';
import CustomerProfile from './pages/customer/CustomerProfile';
import FranchiseProfile from './pages/franchise/FranchiseProfile';
import ProductManagement from './pages/employee/ProductManagement';
import BookingManagement from './pages/employee/BookingManagement';
import BookingFormPage from './pages/employee/bookings/BookingFormPage';
import TrackingManagement from './pages/employee/TrackingManagement';
import TrackShipment from './pages/customer/TrackShipment';
import CustomerBookingForm from './pages/customer/CustomerBookingForm';
import CustomerBookings from './pages/customer/CustomerBookings';
import CustomerInvoices from './pages/customer/CustomerInvoices';
import CustomerInvoiceView from './pages/customer/CustomerInvoiceView';
// Import invoice management components
import InvoiceManagement from './pages/employee/InvoiceManagement';
import InvoiceDetails from './pages/employee/invoices/InvoiceDetails';
// Import activity management components
import ActivityManagement from './pages/employee/ActivityManagement';
import VehicleManagement from './pages/employee/VehicleManagement';
import EmailSettings from './pages/employee/settings/EmailSettings';
// Import delivery run sheet components
import DeliveryRunSheet from './pages/employee/DeliveryRunSheet';
import DeliveryConfirmation from './pages/employee/DeliveryConfirmation';
import DRSList from './pages/employee/DRSList';
import TransitPlanner from './pages/employee/TransitPlanner';
// Import reports components
import Reports from './pages/employee/Reports';

// Create theme
const theme = createTheme({
    palette: {
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
        background: {
            default: '#f5f5f5',
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
});

function App() {
    useEffect(() => {
        // Hide the initial loading screen once React has mounted
        const loader = document.getElementById('initial-loader');
        if (loader) {
            loader.style.display = 'none';
        }
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <Router 
                basename="/admin"
                future={{
                    v7_relativeSplatPath: true,
                    v7_startTransition: true
                }}
            >
                <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/" element={<Navigate to="/login" />} />

                    {/* Employee routes with dynamic permission checking */}
                    <Route path="/employee/dashboard" element={
                        <ProtectedRoute userType="employee">
                            <EmployeeDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee" element={<Navigate to="/employee/dashboard" />} />
                    
                    {/* Booking Management Routes */}
                    <Route path="/employee/bookings" element={
                        <ProtectedRoute userType="employee" requiredPermission="view-bookings">
                            <BookingManagement />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/bookings/create" element={
                        <ProtectedRoute userType="employee" requiredPermission="create-bookings">
                            <BookingFormPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/bookings/edit/:bookingId" element={
                        <ProtectedRoute userType="employee" requiredPermission="edit-bookings">
                            <BookingFormPage />
                        </ProtectedRoute>
                    } />
                    
                    
                    {/* Invoice Management Routes */}
                    <Route path="/employee/invoices" element={
                        <ProtectedRoute userType="employee" requiredPermission="view-invoices">
                            <InvoiceManagement />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/invoices/create" element={
                        <ProtectedRoute userType="employee" requiredPermission="create-invoices">
                            <InvoiceManagement />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/invoices/:id" element={
                        <ProtectedRoute userType="employee" requiredPermission="view-invoices">
                            <InvoiceDetails />
                        </ProtectedRoute>
                    } />
                    
                    {/* User Management Routes */}
                    <Route path="/employee/user-management" element={
                        <ProtectedRoute userType="employee" requiredPermission="manage-users">
                            <UserManagement />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/customers" element={
                        <ProtectedRoute userType="employee" requiredPermission="manage-users">
                            <CustomerManagement />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/franchises" element={
                        <ProtectedRoute userType="employee" requiredPermission="manage-users">
                            <FranchiseManagement />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/branches" element={
                        <ProtectedRoute userType="employee" requiredPermission="manage-users">
                            <BranchManagement />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/location-management" element={
                        <ProtectedRoute userType="employee" requiredPermission="manage-users">
                            <LocationManagement />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/zones" element={
                        <ProtectedRoute userType="employee" requiredPermission="manage-users">
                            <ZoneManagement />
                        </ProtectedRoute>
                    } />
                    {/* Regions are only part of rating under the legacy engine.
                        Bookmarks and typed URLs land on Zone Management instead
                        of a screen that no longer affects any price. */}
                    <Route path="/employee/regions" element={
                        regionsAreUsed() ? (
                            <ProtectedRoute userType="employee" requiredPermission="manage-users">
                                <RegionManagement />
                            </ProtectedRoute>
                        ) : (
                            <Navigate to="/employee/zones" replace />
                        )
                    } />
                    
                    {/* Product Management Routes */}
                    <Route path="/employee/products" element={
                        <ProtectedRoute userType="employee" requiredPermission="manage-users">
                            <ProductManagement />
                        </ProtectedRoute>
                    } />
                    
                    {/* Activity Management Routes */}
                    <Route path="/employee/activities" element={
                        <ProtectedRoute userType="employee" requiredPermission="manage-users">
                            <ActivityManagement />
                        </ProtectedRoute>
                    } />

                    {/* Vehicle Management Routes */}
                    <Route path="/employee/vehicles" element={
                        <ProtectedRoute userType="employee" requiredPermission="manage-users">
                            <VehicleManagement />
                        </ProtectedRoute>
                    } />
                    
                    {/* Reports Routes */}
                    <Route path="/employee/reports" element={<Navigate to="/employee/reports/gst" replace />} />
                    <Route path="/employee/reports/:reportKey" element={
                        <ProtectedRoute userType="employee" requiredPermission="view-reports">
                            <Reports />
                        </ProtectedRoute>
                    } />

                    {/* Settings Routes */}
                    <Route path="/employee/settings/email" element={
                        <ProtectedRoute userType="employee" requiredPermission="manage-settings">
                            <EmailSettings />
                        </ProtectedRoute>
                    } />

                    {/* Customer routes */}
                    <Route path="/customer/dashboard" element={
                        <ProtectedRoute userType="customer">
                            <CustomerDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/customer" element={<Navigate to="/customer/dashboard" />} />
                    <Route path="/customer/profile" element={
                        <ProtectedRoute userType="customer">
                            <CustomerProfile />
                        </ProtectedRoute>
                    } />
                    <Route path="/customer/shipments" element={
                        <ProtectedRoute userType="customer">
                            <CustomerBookings />
                        </ProtectedRoute>
                    } />
                    <Route path="/customer/history" element={
                        <ProtectedRoute userType="customer">
                            <CustomerDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/customer/track" element={
                        <ProtectedRoute userType="customer">
                            <TrackShipment />
                        </ProtectedRoute>
                    } />
                    <Route path="/customer/create-booking" element={
                        <ProtectedRoute userType="customer">
                            <CustomerBookingForm />
                        </ProtectedRoute>
                    } />
                    <Route path="/customer/invoices" element={
                        <ProtectedRoute userType="customer">
                            <CustomerInvoices />
                        </ProtectedRoute>
                    } />
                    <Route path="/customer/invoices/:id" element={
                        <ProtectedRoute userType="customer">
                            <CustomerInvoiceView />
                        </ProtectedRoute>
                    } />
                    <Route path="/customer/settings" element={
                        <ProtectedRoute userType="customer">
                            <CustomerDashboard />
                        </ProtectedRoute>
                    } />

                    {/* Franchise routes */}
                    <Route path="/franchise/dashboard" element={
                        <ProtectedRoute userType="franchise">
                            <FranchiseDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/franchise" element={<Navigate to="/franchise/dashboard" />} />
                    <Route path="/franchise/profile" element={
                        <ProtectedRoute userType="franchise">
                            <FranchiseProfile />
                        </ProtectedRoute>
                    } />
                    <Route path="/franchise/shipments" element={
                        <ProtectedRoute userType="franchise">
                            <FranchiseDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/franchise/customers" element={
                        <ProtectedRoute userType="franchise">
                            <FranchiseDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/franchise/reports" element={
                        <ProtectedRoute userType="franchise">
                            <FranchiseDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/franchise/revenue" element={
                        <ProtectedRoute userType="franchise">
                            <FranchiseDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/franchise/settings" element={
                        <ProtectedRoute userType="franchise">
                            <FranchiseDashboard />
                        </ProtectedRoute>
                    } />

                    {/* Tracking routes */}
                    <Route path="/employee/trackings" element={
                        <ProtectedRoute userType="employee">
                            <TrackingManagement />
                        </ProtectedRoute>
                    } />
                    <Route path="/customer/track-shipment" element={
                        <ProtectedRoute userType="customer" requiredPermission="track-shipment">
                            <TrackShipment />
                        </ProtectedRoute>
                    } />

                    {/* Delivery Run Sheet routes */}
                    <Route path="/employee/delivery-run-sheet" element={
                        <ProtectedRoute userType="employee" requiredPermission="view-bookings">
                            <DeliveryRunSheet />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/drs-list" element={
                        <ProtectedRoute userType="employee" requiredPermission="view-bookings">
                            <DRSList />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/transit-planner" element={
                        <ProtectedRoute userType="employee" requiredPermission="view-bookings">
                            <TransitPlanner key="transit-active" scope="active" />
                        </ProtectedRoute>
                    } />
                    {/* Separate route, not a tab: the two views read the same window in
                        opposite directions, so each keeps its own filter state. */}
                    <Route path="/employee/transit-planner/overdue" element={
                        <ProtectedRoute userType="employee" requiredPermission="view-bookings">
                            <TransitPlanner key="transit-overdue" scope="overdue" />
                        </ProtectedRoute>
                    } />
                    <Route path="/employee/delivery-confirmation" element={
                        <ProtectedRoute userType="employee" requiredPermission="view-bookings">
                            <DeliveryConfirmation />
                        </ProtectedRoute>
                    } />

                    {/* Unauthorized access */}
                    <Route path="/unauthorized" element={<Unauthorized />} />

                    {/* Catch all */}
                    <Route path="*" element={<Navigate to="/login" />} />
                </Routes>
            </Router>
        </ThemeProvider>
    );
}

export default App;
